/**
 * Monochrome icon set — 16×16, single stroke, `currentColor`.
 *
 * These replace the Unicode characters that were standing in for icons:
 * ⚙ ☀ ❄ ⚡ ⚠ ★. Every one of those has an EMOJI PRESENTATION VARIANT, so
 * the same source renders as a flat glyph on one platform and as a coloured
 * sticker on another — a pictograph that carries no brand colour, changes
 * size with the font's fallback, and cannot be sized to the house type
 * scale. These inherit `currentColor`, so they sit in the palette and
 * invert with the theme like everything else.
 *
 * The shapes are deliberately abstract. At 12–15 px a detailed illustration
 * turns to mud; a silhouette is legible and honest about what it is.
 *
 * The purely GEOMETRIC characters (▦ ◫ ⌗ ▴ ▾ ◉ ⬡ ✚ ▲ ◆ ◑) are left alone
 * on purpose: they have no emoji form, they are typographic notation rather
 * than pictographs, and they read as glyphs at this size.
 */
import type { ReactElement } from "react";

interface IconProps {
  size?: number;
  className?: string;
}

function svg(children: ReactElement, { size = 14, className }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** ⚙ — settings. A gear reduced to a ring and eight teeth. */
export function GearIcon(props: IconProps) {
  return svg(
    <>
      <circle cx="8" cy="8" r="2.6" />
      <path d="M8 1.5v2M8 12.5v2M1.5 8h2M12.5 8h2M3.4 3.4l1.4 1.4M11.2 11.2l1.4 1.4M12.6 3.4l-1.4 1.4M4.8 11.2l-1.4 1.4" />
    </>,
    props,
  );
}

/** ☀ — sun. A disc with eight rays, for the light theme. */
export function SunIcon(props: IconProps) {
  return svg(
    <>
      <circle cx="8" cy="8" r="3" />
      <path d="M8 1v1.8M8 13.2V15M1 8h1.8M13.2 8H15M3 3l1.3 1.3M11.7 11.7L13 13M13 3l-1.3 1.3M4.3 11.7L3 13" />
    </>,
    props,
  );
}

/** ❄ — cooling. A six-spoke flake with two barbs on each spoke. */
export function SnowIcon(props: IconProps) {
  return svg(
    <>
      <path d="M8 1.5v13M2.2 4.75l11.6 6.5M13.8 4.75l-11.6 6.5" />
      <path d="M6.2 3.3 8 4.6l1.8-1.3M6.2 12.7 8 11.4l1.8 1.3M3.4 6.4 5.2 7l-.4 2.1M12.6 6.4 10.8 7l.4 2.1M5.2 9.9l1.8-.6-.4-2.1M10.8 9.9 9 9.3l.4-2.1" />
    </>,
    props,
  );
}

/** ⚡ — power. A bolt, drawn as a stroke rather than a filled polygon so it
 *  matches the weight of everything else in the set. */
export function BoltIcon(props: IconProps) {
  return svg(<path d="M9 1.5 3.5 9H7.5l-.5 5.5L12.5 7H8.5z" />, props);
}

/** ⚠ — warning. A triangle with a bar and a dot. */
export function WarnIcon(props: IconProps) {
  return svg(
    <>
      <path d="M8 2 15 13.5H1z" />
      <path d="M8 6.5v3.2" />
      <circle cx="8" cy="11.6" r="0.5" fill="currentColor" stroke="none" />
    </>,
    props,
  );
}

/** ★ — reference marker (a figure held to a static reference table). */
export function RefMark(props: IconProps) {
  return svg(
    <path d="M8 1.8l1.8 3.9 4.2.5-3.1 2.9.85 4.2L8 11.3l-3.75 2 .85-4.2L2 6.2l4.2-.5z" />,
    props,
  );
}

/** ◑ — half-lit disc: the light theme. */
export function HalfIcon(props: IconProps) {
  return svg(
    <>
      <circle cx="8" cy="8" r="5.2" />
      <path d="M8 2.8a5.2 5.2 0 010 10.4z" fill="currentColor" stroke="none" />
    </>,
    props,
  );
}

/** ◆ — solid diamond: the high-contrast theme. */
export function DiamondIcon(props: IconProps) {
  return svg(<path d="M8 1.6 14.4 8 8 14.4 1.6 8z" />, props);
}
